<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(title="Edit claim amount" :loading="isLoading" @back="onCloseDialog")
        template(#actions)
          .d-flex.align-center(style="gap: 12px")
            yz-btn(size="lg" text label="Cancel" :disabled="isLoading" @click="onCloseDialog")
            yz-tooltip(:text="saveTooltipText" :disabled="!isSaveDisabled" direction="left")
              yz-btn(size="lg" label="Save" :loading="isLoading" :disabled="isSaveDisabled" @click="onSaveChanges")
      yz-dialog-fullscreen-content.pa-6
        // Header Summary Section
        yz-card.pa-3.mb-6(color="yz-neutral-300" outlined style="border-radius: 10px;")
          v-row(no-gutters align="center")
            v-col(cols="auto")
              .d-flex.align-center.gap-2.mb-2
                .yz-medium-body-sm.text-neutral-700 {{ parentClaim.panel.name }}
                yz-divider(vertical color="yz-neutral-400")
                .yz-medium-body-xs.text-neutral-500 Claim {{ '#' + parentClaim.rid }}
              .yz-heading-6.text-neutral-400 {{ originalTotalAmount | displayPrice($currency) }}

            v-spacer

            v-col(cols="auto")
              .text-right
                .yz-medium-body-sm.text-neutral-700.mb-1 New claim amount
                .yz-heading-6.text-neutral-700 {{ newTotalAmount | displayPrice($currency) }}

            v-col.ml-5(cols="auto" style="border-left: 1px solid #C1C1C1; padding-left: 20px;")
              .text-right
                .yz-medium-body-sm.text-neutral-700.mb-1 Total adjustments
                .yz-heading-6(:class="totalAdjustment >= 0 ? 'text-success' : 'text-danger'") {{ totalAdjustment < 0 ? '-' : '' }}{{ Math.abs(totalAdjustment) | displayPrice($currency) }}

        // Claims Table
        yz-card(outlined)
          .pa-4
            yz-data-table(:headers="tableHeaders" :items="invoicesData" :loading="isFetching" :items-per-page="pageSize" :page="currentPage" disable-sort)
              template(v-slot:item.invoiceNo="{ item }")
                .yz-medium-body-sm {{ item.invoiceNo }}

              template(v-slot:item.submittedDate="{ item }")
                .yz-medium-body-sm {{ item.submittedDate }}

              template(v-slot:item.currentAmount="{ item }")
                .yz-medium-body-sm {{ item.currentAmount | displayPrice($currency) }}

              template(v-slot:item.newAmount="{ item }")
                yz-text-field.my-2(v-model.number="item.newAmount" type="number" min="0" size="md" :prefix="$currency.symbol" :rules="newAmountRules" required required-error-message="New amount is required")

              template(v-slot:item.adjustment="{ item }")
                yz-text-field(:value="(item.adjustment / $currency.multiplier).toFixed(2)" readonly background-color="yz-neutral-200" hide-details size="md" :prefix="$currency.symbol")

            // Pagination
            .d-flex.justify-end.mt-4
              yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="item" arrow)

    // Confirmation Dialog
    edit-bulk-claim-amount-confirmation-dialog(v-if="showConfirmationDialog" v-model="showConfirmationDialog" :original-total-amount="originalTotalAmount" :new-total-amount="newTotalAmount" :total-adjustment="totalAdjustment" @confirmed="onConfirmationConfirmed" @canceled="onConfirmationCanceled")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import { validateDecimalPlaces, hasExcessiveDecimals } from '@/utils'
import EditBulkClaimAmountConfirmationDialog from '@/components/clinic/panel/dialogs/bulk/EditBulkClaimAmountConfirmationDialog.vue'

export default {
  components: { EditBulkClaimAmountConfirmationDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isFetching: true,
      isError: false,
      currentPage: 1,
      pageSize: 20,
      showConfirmationDialog: false,

      // Table headers
      tableHeaders: [
        { text: 'Invoice no', value: 'invoiceNo', width: '200px' },
        { text: 'Submitted date', value: 'submittedDate', width: '200px' },
        { text: 'Current claimed amount', value: 'currentAmount', width: '200px' },
        { text: 'New claimed amount', value: 'newAmount', width: '200px' },
        { text: 'Adjustments', value: 'adjustment', width: '200px' }
      ],

      // Invoice data from API
      invoicesData: [],

      // Validation rules for new amount
      newAmountRules: [
        v => v != null && v !== '' || 'New amount is required',
        v => parseFloat(v) > 0 || 'Amount must be greater than 0',
        v => validateDecimalPlaces(v, this.$currency)
      ]
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    originalTotalAmount() {
      // currentAmount is already in cents from API
      return this.invoicesData.reduce((sum, invoice) => sum + parseFloat(invoice.currentAmount || 0), 0)
    },
    newTotalAmount() {
      // User inputs newAmount in display format, convert to cents
      return this.invoicesData.reduce((sum, invoice) => {
        const newAmount = invoice.newAmount != null && invoice.newAmount !== ''
          ? Math.round(parseFloat(invoice.newAmount) * this.$currency.multiplier)
          : 0
        return sum + newAmount
      }, 0)
    },
    totalAdjustment() {
      // Both amounts are in cents now
      return this.newTotalAmount - this.originalTotalAmount
    },
    totalCount() {
      return this.invoicesData.length
    },
    isSaveDisabled() {
      // Check 1: All new amounts must be filled (not null/empty)
      const hasEmptyAmount = this.invoicesData.some(invoice => {
        const amount = invoice.newAmount
        return amount == null || amount === ''
      })

      // Check 2: All new amounts must be > 0 (no zero values)
      const hasZeroOrNegativeAmount = this.invoicesData.some(invoice => {
        return parseFloat(invoice.newAmount) <= 0
      })

      // Check 3: All new amounts must have valid decimal places
      const hasInvalidDecimals = this.invoicesData.some(invoice => {
        return hasExcessiveDecimals(invoice.newAmount, this.$currency)
      })

      // Check 4: At least one invoice must have an adjustment
      const hasNoAdjustments = this.invoicesData.every(invoice => {
        const newAmountInCents = Math.round(parseFloat(invoice.newAmount || 0) * this.$currency.multiplier)
        const currentAmountInCents = parseFloat(invoice.currentAmount || 0)
        return newAmountInCents === currentAmountInCents
      })

      return hasEmptyAmount || hasZeroOrNegativeAmount || hasInvalidDecimals || hasNoAdjustments || this.isFetching
    },
    saveTooltipText() {
      // Check for empty amounts
      const hasEmptyAmount = this.invoicesData.some(invoice => {
        const amount = invoice.newAmount
        return amount == null || amount === ''
      })

      if (hasEmptyAmount) {
        return 'Please fill in all claim amounts'
      }

      // Check for zero or negative amounts
      const hasZeroOrNegativeAmount = this.invoicesData.some(invoice => {
        return parseFloat(invoice.newAmount) <= 0
      })

      if (hasZeroOrNegativeAmount) {
        return 'All claim amounts must be greater than 0'
      }

      // Check for excessive decimal places
      const hasInvalidDecimals = this.invoicesData.some(invoice => {
        return hasExcessiveDecimals(invoice.newAmount, this.$currency)
      })

      if (hasInvalidDecimals) {
        return `Maximum ${this.$currency.exponent} decimal places allowed`
      }

      // Check for no adjustments
      const hasNoAdjustments = this.invoicesData.every(invoice => {
        const newAmountInCents = Math.round(parseFloat(invoice.newAmount || 0) * this.$currency.multiplier)
        const currentAmountInCents = parseFloat(invoice.currentAmount || 0)
        return newAmountInCents === currentAmountInCents
      })

      if (hasNoAdjustments) {
        return 'Change at least one claim amount'
      }

      return ''
    }
  },
  watch: {
    // Recalculate adjustments when amounts change
    invoicesData: {
      handler() {
        this.invoicesData.forEach(invoice => {
          const newAmountInCents = invoice.newAmount != null && invoice.newAmount !== ''
            ? Math.round(parseFloat(invoice.newAmount) * this.$currency.multiplier)
            : 0
          const currentAmountInCents = parseFloat(invoice.currentAmount || 0)
          // Store adjustment in cents
          invoice.adjustment = newAmountInCents - currentAmountInCents
        })
      },
      deep: true
    }
  },
  mounted() {
    if (this.parentClaim && this.parentClaim.invoices && this.parentClaim.invoices.length > 0) {
      this.fetchAllInvoices()
    } else {
      this.isError = true
      this.isFetching = false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    sanitizeData(invoice) {
      // Format submitted date from parent claim
      let submittedDate = ''
      if (this.parentClaim && this.parentClaim.submitted_date) {
        try {
          const dt = DateTime.fromISO(this.parentClaim.submitted_date)
          submittedDate = dt.toFormat('dd MMMM yyyy')
        } catch (error) {
          console.error('Error formatting date:', error)
          submittedDate = this.parentClaim.submitted_date
        }
      }

      return {
        uid: invoice.uid,
        invoiceNo: invoice.invoice_no?.toString() || '',
        submittedDate,
        currentAmount: invoice.amount,
        newAmount: invoice.amount / this.$currency.multiplier,
        adjustment: 0,
        originalInvoice: invoice
      }
    },
    async fetchAllInvoices() {
      // Fetch all invoices at once for client-side pagination
      this.isFetching = true
      this.isError = false

      try {
        // Extract invoice UIDs from parentClaim.invoices (already an array of UID strings)
        const invoiceUids = this.parentClaim.invoices.join(',')

        // Get panel ID from parentClaim
        const panelId = this.parentClaim?.panel?.id

        const params = {
          uids: invoiceUids,
          page_size: -1, // Fetch all invoices
          panel: panelId,
          fields: ['uid', 'invoice_no', 'amount']
        }

        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })

        // Transform and store the fetched invoice data
        if (data.count > 0) {
          this.invoicesData = data.results.map(invoice => this.sanitizeData(invoice))
        } else {
          this.isError = true
          console.error('No invoices found for the selected UIDs.')
          await this.showMessage(['error', 'No invoices found for the selected items. Please check and try again.'])
          this.invoicesData = []
        }
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    onCloseDialog() {
      this.$emit('input', false)
      this.$emit('cancel')
    },
    onSaveChanges() {
      this.isLoading = true
      this.showConfirmationDialog = true
    },
    onConfirmationConfirmed(confirmData) {
      // Format payload for API patch request
      const invoicesPayload = this.invoicesData.map(invoice => ({
        uid: invoice.uid,
        amount: Math.round(parseFloat(invoice.newAmount) * this.$currency.multiplier) // Convert user input to cents
      }))

      const finalData = {
        totalSubmittedAmount: this.newTotalAmount, // Already in cents from computed property
        remarks: confirmData.remarks,
        invoices: invoicesPayload
      }

      this.$emit('confirmed', finalData)
    },
    onConfirmationCanceled() {
      this.isLoading = false
      this.showConfirmationDialog = false
    }
  }
}
</script>

<style lang="scss" scoped></style>
